import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  FaRedo,
  FaCalendarAlt,
  FaMousePointer,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaExclamationTriangle,
  FaQuestionCircle,
  FaSpinner,
  FaSyncAlt,
  FaHistory,
} from "react-icons/fa";

import { getNotificationsByUser } from "../../../../../services/notificationServices";

import {
  getAdherentById,
  findAdherentIdByFullName,
} from "../../../../../services/adherentServices";

import { getReglementInscriptionByIdAndSaison } from "../../../../../services/reglementinscriptionServices";

import ReglementInscriptionDrawer from "./ReglementInscriptionDrawer";

const API_BASE =
  process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const DEBUG_RENEWED_MEMBERS = true;

/* -------------------------------------------------------------------------- */
/*                                   Debug                                    */
/* -------------------------------------------------------------------------- */

function debugLog(...args) {
  if (DEBUG_RENEWED_MEMBERS) {
    console.log(...args);
  }
}

function debugWarn(...args) {
  if (DEBUG_RENEWED_MEMBERS) {
    console.warn(...args);
  }
}

function debugError(...args) {
  if (DEBUG_RENEWED_MEMBERS) {
    console.error(...args);
  }
}

/* -------------------------------------------------------------------------- */
/*                            Données utilisateur                              */
/* -------------------------------------------------------------------------- */

function getCurrentUserId() {
  try {
    const rawUserInfo = localStorage.getItem("userInfo");

    if (!rawUserInfo) {
      return null;
    }

    const userInfo = JSON.parse(rawUserInfo);

    const userId = userInfo?.userId || userInfo?.id || null;

    return userId ? Number(userId) : null;
  } catch (error) {
    debugError(
      "[RenewedMembers] Impossible de lire userInfo dans localStorage :",
      error,
    );

    return null;
  }
}

/* -------------------------------------------------------------------------- */
/*                                   Saison                                   */
/* -------------------------------------------------------------------------- */

function getCurrentSeason() {
  const now = new Date();
  const year = now.getFullYear();

  const seasonStart = new Date(`${year}-08-01T00:00:00`);

  if (now >= seasonStart) {
    return `${year}-${year + 1}`;
  }

  return `${year - 1}-${year}`;
}

/* -------------------------------------------------------------------------- */
/*                              Notifications                                 */
/* -------------------------------------------------------------------------- */

function extractNotifications(data) {
  if (Array.isArray(data)) {
    return data;
  }

  if (data && typeof data === "object") {
    if (Array.isArray(data.notifications)) {
      return data.notifications;
    }

    if (Array.isArray(data.rows)) {
      return data.rows;
    }

    if (Array.isArray(data.items)) {
      return data.items;
    }

    if (Array.isArray(data.data)) {
      return data.data;
    }
  }

  return [];
}

/**
 * Exemple :
 *
 * Mathys Forestier a effectué le renouvellement de Mathys FORESTIER
 * pour la saison 2026-2027 sur le(s) groupe(s) :
 * Adultes (2026-2027).
 */
function parseRenewalNotification(notification) {
  const message = String(notification?.message || "").trim();

  if (!message) {
    return null;
  }

  if (
    !message
      .toLowerCase()
      .includes("a effectué le renouvellement")
  ) {
    return null;
  }

  const match = message.match(
    /a effectué le renouvellement de\s+(.+?)\s+pour la saison\s+(\d{4}-\d{4})(?:\s+sur le\(s\) groupe\(s\)\s*:\s*(.+))?$/i,
  );

  if (!match) {
    debugWarn(
      "[RenewedMembers] Notification de renouvellement non reconnue :",
      message,
    );

    return null;
  }

  const fullName = String(match[1] || "")
    .trim()
    .replace(/\s+/g, " ");

  const saison = String(match[2] || "").trim();

  const groupsLabel = String(match[3] || "")
    .trim()
    .replace(/\.\s*$/, "");

  return {
    notificationId: notification?.id,
    fullName,
    saison,
    groupsLabel,
    renewedAt:
      notification?.created_at ||
      notification?.createdAt ||
      null,
    originalNotification: notification,
  };
}

function normalizePersonKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

/* -------------------------------------------------------------------------- */
/*                                 Adhérent                                   */
/* -------------------------------------------------------------------------- */

function extractAdherent(data) {
  if (!data) {
    return null;
  }

  if (Array.isArray(data)) {
    return data[0] || null;
  }

  if (data?.adherent) {
    return data.adherent;
  }

  if (data?.member) {
    return data.member;
  }

  if (Array.isArray(data?.data)) {
    return data.data[0] || null;
  }

  if (data?.data && typeof data.data === "object") {
    return data.data;
  }

  if (typeof data === "object") {
    return data;
  }

  return null;
}

function getAdherentId(adherent) {
  return (
    adherent?.id ||
    adherent?.adherent_id ||
    adherent?.adherentId ||
    null
  );
}

/* -------------------------------------------------------------------------- */
/*                                  Photos                                    */
/* -------------------------------------------------------------------------- */

function toAbsoluteUrl(url) {
  if (!url) {
    return "";
  }

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
}

function fallbackPhoto(genre) {
  return genre === "Féminin"
    ? "/img/judokate_avatar.png"
    : "/img/judoka_avatar.png";
}

function getPhoto(adherent) {
  const photo =
    adherent?.photo_url ||
    adherent?.photoURL ||
    adherent?.photo ||
    adherent?.image_url ||
    "";

  if (!photo || photo === "/img/default_avatar.png") {
    return fallbackPhoto(adherent?.genre);
  }

  return toAbsoluteUrl(photo);
}

/* -------------------------------------------------------------------------- */
/*                                  Affichage                                 */
/* -------------------------------------------------------------------------- */

function getDisplayName(adherent) {
  const nom = String(adherent?.nom || "")
    .trim()
    .toUpperCase();

  const prenom = String(adherent?.prenom || "").trim();

  return `${nom} ${prenom}`.trim() || "Adhérent";
}

function formatDateTime(dateValue) {
  if (!dateValue) {
    return "—";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function formatDate(dateValue) {
  if (!dateValue) {
    return "—";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/* -------------------------------------------------------------------------- */
/*                                  Statuts                                   */
/* -------------------------------------------------------------------------- */

function normalizeStatus(status) {
  return String(status || "")
    .trim()
    .replace("Cours d’essai", "Cours d'essai");
}

function getStatusClasses(status) {
  switch (normalizeStatus(status)) {
    case "Licencié":
      return "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20";

    case "Licencié Hors Club":
      return "bg-teal-50 text-teal-700 ring-teal-200 dark:bg-teal-500/10 dark:text-teal-300 dark:ring-teal-500/20";

    case "Inscrit":
      return "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20";

    case "Cours d'essai":
      return "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20";

    case "Licencié - Arrêt":
    case "Ancien Licencié et Arrêt":
    case "Non Licencié - Arrêt":
      return "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700";

    case "Ancien Licencié":
      return "bg-slate-900 text-white ring-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:ring-slate-100";

    default:
      return "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20";
  }
}

/* -------------------------------------------------------------------------- */
/*                        Badge règlement inscription                         */
/* -------------------------------------------------------------------------- */

function ReglementBadge({ status }) {
  const config = {
    loading: {
      icon: <FaSpinner className="animate-spin" />,
      className:
        "bg-slate-200 text-slate-500 dark:bg-slate-700 dark:text-slate-300",
      title: "Vérification du règlement d'inscription...",
    },

    yes: {
      icon: <FaCheckCircle />,
      className:
        "bg-emerald-500 text-white ring-2 ring-white dark:ring-slate-950",
      title: "Règlement d'inscription déjà affecté",
    },

    no: {
      icon: <FaExclamationTriangle />,
      className:
        "bg-amber-500 text-white ring-2 ring-white dark:ring-slate-950",
      title:
        "Aucun règlement d'inscription pour la saison en cours",
    },

    error: {
      icon: <FaQuestionCircle />,
      className:
        "bg-slate-400 text-white ring-2 ring-white dark:ring-slate-950",
      title:
        "Statut du règlement inconnu",
    },
  };

  const current = config[status] || config.loading;

  return (
    <span
      title={current.title}
      className={`absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded-full text-[11px] shadow-sm ${current.className}`}
    >
      {current.icon}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Carte adhérent                               */
/* -------------------------------------------------------------------------- */

function RenewedMemberCard({
  adherent,
  onOpen,
  reglementStatus,
}) {
  const name = getDisplayName(adherent);

  const photo = getPhoto(adherent);

  const status = normalizeStatus(
    adherent?.statut_inscription,
  );

  const renewedAt = adherent?.renewedAt;

  const groupsLabel =
    adherent?.renewalGroupsLabel || "";

  const originalCreatedAt =
    adherent?.createdAt ||
    adherent?.created_at ||
    null;

  return (
    <button
      type="button"
      onClick={() => onOpen?.(adherent)}
      className="group flex min-h-[128px] overflow-hidden rounded-3xl border border-slate-200 bg-white text-left shadow-sm transition hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-emerald-500/15 dark:border-slate-800 dark:bg-slate-950/60 dark:hover:border-emerald-500/50"
      title="Ouvrir la préparation du règlement d'inscription"
    >
      <div className="relative h-auto w-24 shrink-0 overflow-hidden sm:w-28">
        <img
          src={photo}
          alt={name}
          loading="lazy"
          className="h-full min-h-[128px] w-full object-cover transition duration-300 group-hover:scale-105"
          onError={(event) => {
            event.currentTarget.src =
              fallbackPhoto(adherent?.genre);
          }}
        />

        <ReglementBadge
          status={reglementStatus}
        />

        <span
          title="Renouvellement"
          className="absolute bottom-2 left-2 flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-xs text-white ring-2 ring-white shadow-sm dark:ring-slate-950"
        >
          <FaSyncAlt />
        </span>
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-between p-3">
        <div className="min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4 className="line-clamp-2 text-sm font-bold leading-5 text-slate-900 dark:text-white">
              {name}
            </h4>

            <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-[11px] text-emerald-700 opacity-0 transition group-hover:opacity-100 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaMousePointer />
            </span>
          </div>

          <p className="mt-1 flex items-center gap-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
            <FaCalendarAlt className="shrink-0 text-[10px]" />

            <span className="truncate">
              Renouvelé le{" "}
              {formatDateTime(renewedAt)}
            </span>
          </p>

          {originalCreatedAt ? (
            <p
              className="mt-1 flex items-center gap-1.5 text-[10px] text-slate-400 dark:text-slate-500"
              title={`Fiche créée le ${formatDateTime(
                originalCreatedAt,
              )}`}
            >
              <FaHistory />

              Fiche créée le{" "}
              {formatDate(originalCreatedAt)}
            </p>
          ) : null}
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          {status ? (
            <span
              className={`inline-flex max-w-full items-center rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${getStatusClasses(
                status,
              )}`}
              title={status}
            >
              <span className="truncate">
                {status}
              </span>
            </span>
          ) : null}

          {groupsLabel ? (
            <span
              className="inline-flex max-w-full items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
              title={groupsLabel}
            >
              <span className="truncate">
                {groupsLabel}
              </span>
            </span>
          ) : (
            <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:ring-slate-700">
              Groupe non précisé
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 Skeleton                                   */
/* -------------------------------------------------------------------------- */

function SkeletonCard() {
  return (
    <div className="flex min-h-[128px] overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950/60">
      <div className="h-auto w-24 shrink-0 animate-pulse bg-slate-200 dark:bg-slate-800 sm:w-28" />

      <div className="min-w-0 flex-1 space-y-2 p-3">
        <div className="h-4 w-2/3 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />

        <div className="h-3 w-1/2 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />

        <div className="h-3 w-1/3 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />

        <div className="mt-4 flex gap-2">
          <div className="h-5 w-20 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

          <div className="h-5 w-24 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                Empty state                                 */
/* -------------------------------------------------------------------------- */

function EmptyState({ saison }) {
  return (
    <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950/50">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
        <FaSyncAlt />
      </div>

      <h3 className="mt-3 text-base font-bold text-slate-900 dark:text-white">
        Aucun renouvellement
      </h3>

      <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
        Aucun renouvellement n'a été détecté pour
        la saison {saison}.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                 Erreur                                     */
/* -------------------------------------------------------------------------- */

function ErrorPanel({ message, onRetry }) {
  return (
    <div className="rounded-[24px] border border-rose-200 bg-rose-50 p-5 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
      <p className="font-bold">
        Erreur de chargement
      </p>

      <p className="mt-1 text-sm leading-6">
        {message}
      </p>

      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-700"
      >
        <FaRedo />

        Réessayer
      </button>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                               Composant                                    */
/* -------------------------------------------------------------------------- */

export default function RenewedMembers() {
  const saison = useMemo(
    () => getCurrentSeason(),
    [],
  );

  const [items, setItems] = useState([]);

  const [visibleCount, setVisibleCount] =
    useState(10);

  const [loading, setLoading] =
    useState(true);

  const [errorMsg, setErrorMsg] =
    useState("");

  const [
    selectedAdherent,
    setSelectedAdherent,
  ] = useState(null);

  const [drawerOpen, setDrawerOpen] =
    useState(false);

  const [reglementMap, setReglementMap] =
    useState({});

  /* ---------------------------------------------------------------------- */
  /*                       Chargement des renouvellements                    */
  /* ---------------------------------------------------------------------- */

  const fetchRenewedMembers =
    useCallback(async () => {
      console.groupCollapsed(
        "[RenewedMembers] fetchRenewedMembers()",
      );

      try {
        setLoading(true);
        setErrorMsg("");

        const userId =
          getCurrentUserId();

        debugLog(
          "[RenewedMembers] userId :",
          userId,
        );

        if (!userId) {
          throw new Error(
            "Impossible de récupérer l'identifiant de l'utilisateur connecté.",
          );
        }

        /*
         * 1. Notifications de l'administrateur
         */
        const notificationsResponse =
          await getNotificationsByUser(
            userId,
          );

        const notifications =
          extractNotifications(
            notificationsResponse,
          );

        debugLog(
          "[RenewedMembers] notifications :",
          notifications,
        );

        /*
         * 2. Détection des notifications de renouvellement
         */
        const parsedRenewals =
          notifications
            .map(
              parseRenewalNotification,
            )
            .filter(Boolean)
            .filter(
              (renewal) =>
                renewal.saison === saison,
            )
            .sort((a, b) => {
              return (
                new Date(
                  b.renewedAt || 0,
                ).getTime() -
                new Date(
                  a.renewedAt || 0,
                ).getTime()
              );
            });

        debugLog(
          `[RenewedMembers] renouvellements ${saison} :`,
          parsedRenewals,
        );

        /*
         * 3. Suppression des doublons.
         *
         * Si plusieurs notifications concernent
         * le même adhérent, on conserve la plus
         * récente.
         */
        const uniqueByPerson =
          new Map();

        parsedRenewals.forEach(
          (renewal) => {
            const key =
              normalizePersonKey(
                renewal.fullName,
              );

            if (
              !uniqueByPerson.has(key)
            ) {
              uniqueByPerson.set(
                key,
                renewal,
              );
            }
          },
        );

        const uniqueRenewals =
          Array.from(
            uniqueByPerson.values(),
          );

        debugLog(
          "[RenewedMembers] renouvellements uniques :",
          uniqueRenewals,
        );

        /*
         * 4. Pour chaque personne :
         *
         * notification
         *     ↓
         * fullName
         *     ↓
         * findAdherentIdByFullName()
         *     ↓
         * id
         *     ↓
         * getAdherentById()
         */
        const results =
          await Promise.allSettled(
            uniqueRenewals.map(
              async (renewal) => {
                debugLog(
                  "[RenewedMembers] recherche :",
                  renewal.fullName,
                );

                const identity =
                  await findAdherentIdByFullName(
                    renewal.fullName,
                  );

                const adherentId =
                  identity?.id;

                if (!adherentId) {
                  debugWarn(
                    "[RenewedMembers] adhérent introuvable :",
                    renewal.fullName,
                  );

                  return null;
                }

                const adherentResponse =
                  await getAdherentById(
                    adherentId,
                  );

                const adherent =
                  extractAdherent(
                    adherentResponse,
                  );

                if (!adherent) {
                  debugWarn(
                    "[RenewedMembers] fiche adhérent vide :",
                    adherentId,
                  );

                  return null;
                }

                return {
                  ...adherent,

                  renewalNotificationId:
                    renewal.notificationId,

                  renewedAt:
                    renewal.renewedAt,

                  renewalSeason:
                    renewal.saison,

                  renewalGroupsLabel:
                    renewal.groupsLabel,

                  renewalMessage:
                    renewal
                      .originalNotification
                      ?.message,
                };
              },
            ),
          );

        const resolvedMembers =
          results
            .filter(
              (result) =>
                result.status ===
                "fulfilled" &&
                result.value,
            )
            .map(
              (result) =>
                result.value,
            )
            .sort((a, b) => {
              return (
                new Date(
                  b.renewedAt || 0,
                ).getTime() -
                new Date(
                  a.renewedAt || 0,
                ).getTime()
              );
            });

        /*
         * 5. Sécurité supplémentaire :
         * dédoublonnage par ID adhérent.
         */
        const membersById =
          new Map();

        resolvedMembers.forEach(
          (member) => {
            const id =
              getAdherentId(member);

            if (
              id &&
              !membersById.has(id)
            ) {
              membersById.set(
                id,
                member,
              );
            }
          },
        );

        const finalMembers =
          Array.from(
            membersById.values(),
          );

        debugLog(
          "[RenewedMembers] adhérents finaux :",
          finalMembers,
        );

        if (
          DEBUG_RENEWED_MEMBERS &&
          finalMembers.length > 0
        ) {
          console.table(
            finalMembers.map(
              (member) => ({
                id: getAdherentId(
                  member,
                ),
                nom: member?.nom,
                prenom:
                  member?.prenom,
                renewedAt:
                  member?.renewedAt,
                saison:
                  member?.renewalSeason,
                groupes:
                  member?.renewalGroupsLabel,
                ficheCreee:
                  member?.createdAt ||
                  member?.created_at,
              }),
            ),
          );
        }

        setItems(finalMembers);

        setVisibleCount(10);
      } catch (error) {
        debugError(
          "[RenewedMembers] erreur :",
          error,
        );

        setItems([]);

        setErrorMsg(
          error?.message ||
            "Impossible de récupérer les renouvellements.",
        );
      } finally {
        setLoading(false);

        console.groupEnd();
      }
    }, [saison]);

  useEffect(() => {
    fetchRenewedMembers();
  }, [fetchRenewedMembers]);

  /* ---------------------------------------------------------------------- */
  /*                  Vérification règlement inscription                    */
  /* ---------------------------------------------------------------------- */

  const checkReglementsForItems =
    useCallback(
      async (list) => {
        if (
          !Array.isArray(list) ||
          list.length === 0
        ) {
          setReglementMap({});

          return;
        }

        setReglementMap(
          (previous) => {
            const next = {
              ...previous,
            };

            list.forEach(
              (item) => {
                const id =
                  getAdherentId(
                    item,
                  );

                if (id) {
                  next[id] =
                    "loading";
                }
              },
            );

            return next;
          },
        );

        const results =
          await Promise.allSettled(
            list.map(
              async (item) => {
                const id =
                  getAdherentId(
                    item,
                  );

                if (!id) {
                  return null;
                }

                try {
                  const reglement =
                    await getReglementInscriptionByIdAndSaison(
                      id,
                      saison,
                    );

                  return {
                    id,
                    status:
                      reglement
                        ? "yes"
                        : "no",
                  };
                } catch (error) {
                  debugWarn(
                    "[RenewedMembers] erreur règlement :",
                    id,
                    error,
                  );

                  return {
                    id,
                    status:
                      "error",
                  };
                }
              },
            ),
          );

        setReglementMap(
          (previous) => {
            const next = {
              ...previous,
            };

            results.forEach(
              (result) => {
                if (
                  result.status ===
                    "fulfilled" &&
                  result.value
                ) {
                  next[
                    result.value.id
                  ] =
                    result.value.status;
                }
              },
            );

            return next;
          },
        );
      },
      [saison],
    );

  useEffect(() => {
    checkReglementsForItems(items);
  }, [
    items,
    checkReglementsForItems,
  ]);

  /* ---------------------------------------------------------------------- */
  /*                               Affichage                                 */
  /* ---------------------------------------------------------------------- */

  const displayedItems =
    useMemo(() => {
      return items.slice(
        0,
        visibleCount,
      );
    }, [items, visibleCount]);

  const latestRenewalDate =
    useMemo(() => {
      return (
        items?.[0]?.renewedAt ||
        null
      );
    }, [items]);

  const canShowMore =
    visibleCount < items.length;

  const canShowLess =
    visibleCount > 10;

  const showMore = () => {
    setVisibleCount((previous) =>
      Math.min(
        previous + 10,
        items.length,
      ),
    );
  };

  const showLess = () => {
    setVisibleCount((previous) =>
      Math.max(
        previous - 10,
        10,
      ),
    );
  };

  /* ---------------------------------------------------------------------- */
  /*                                 Drawer                                  */
  /* ---------------------------------------------------------------------- */

  const openDrawer = (adherent) => {
    setSelectedAdherent(adherent);

    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);

    setSelectedAdherent(null);
  };

  /* ---------------------------------------------------------------------- */
  /*                                  Erreur                                 */
  /* ---------------------------------------------------------------------- */

  if (errorMsg) {
    return (
      <ErrorPanel
        message={errorMsg}
        onRetry={
          fetchRenewedMembers
        }
      />
    );
  }

  /* ---------------------------------------------------------------------- */
  /*                                  Render                                 */
  /* ---------------------------------------------------------------------- */

  return (
    <div className="space-y-4">
      {/* --------------------------------------------------------------- */}
      {/* Header                                                          */}
      {/* --------------------------------------------------------------- */}

      <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
              <FaSyncAlt />
            </span>

            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                Renouvellements{" "}
                {saison}
              </h3>

              <p className="text-sm leading-6 text-slate-600 dark:text-slate-300">
                Adhérents ayant
                renouvelé leur
                inscription pour la
                nouvelle saison.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
            {loading
              ? "Chargement..."
              : `${displayedItems.length} / ${items.length} affiché${
                  displayedItems.length >
                  1
                    ? "s"
                    : ""
                }`}
          </span>

          {latestRenewalDate ? (
            <span className="hidden rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300 sm:inline-flex">
              Dernier :{" "}
              {formatDateTime(
                latestRenewalDate,
              )}
            </span>
          ) : null}

          <button
            type="button"
            onClick={
              fetchRenewedMembers
            }
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaRedo
              className={
                loading
                  ? "animate-spin"
                  : ""
              }
            />

            Actualiser
          </button>
        </div>
      </div>

      {/* --------------------------------------------------------------- */}
      {/* Contenu                                                         */}
      {/* --------------------------------------------------------------- */}

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[...Array(10)].map(
            (_, index) => (
              <SkeletonCard
                key={index}
              />
            ),
          )}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          saison={saison}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {displayedItems.map(
            (
              adherent,
              index,
            ) => {
              const adherentId =
                getAdherentId(
                  adherent,
                );

              return (
                <RenewedMemberCard
                  key={
                    adherentId ||
                    index
                  }
                  adherent={
                    adherent
                  }
                  onOpen={
                    openDrawer
                  }
                  reglementStatus={
                    reglementMap[
                      adherentId
                    ] ||
                    "loading"
                  }
                />
              );
            },
          )}
        </div>
      )}

      {/* --------------------------------------------------------------- */}
      {/* Pagination                                                      */}
      {/* --------------------------------------------------------------- */}

      {items.length > 10 ? (
        <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center sm:justify-center">
          {canShowLess ? (
            <button
              type="button"
              onClick={showLess}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              <FaChevronUp />

              Voir moins
            </button>
          ) : null}

          {canShowMore ? (
            <button
              type="button"
              onClick={showMore}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700"
            >
              <FaChevronDown />

              Voir 10 de plus
            </button>
          ) : null}
        </div>
      ) : null}

      {/* --------------------------------------------------------------- */}
      {/* Drawer règlement                                                */}
      {/* --------------------------------------------------------------- */}

      <ReglementInscriptionDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        adherent={
          selectedAdherent
        }
        onCreated={() => {
          debugLog(
            "[RenewedMembers] règlement créé -> actualisation",
          );

          fetchRenewedMembers();
        }}
      />
    </div>
  );
}